import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  getCurrentQuestion,
  getCurrentQuestionInfo,
  getCurrentSelectedAnswer,
  getCurrentSessionUID,
  getIsPendingAction,
  getQuizDetails,
  getQuizQuestionAnswers
} from '@selectors/quizTaking';
import {
  setCurrentSelectedAnswer,
  submitAnswerResponse,
  toggleMarkQuestion
} from '@actions/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import { arrayAreEqual } from '@utils/equalFns';
import { ATTEMPT_SUMMARY_TYPE } from '@cont/Quiz/constants';
import useMaxAttemptQuestions from '@cont/Quiz/Taking/Common/useMaxAttemptQuestions';
import useOngoingSection from '@cont/Quiz/Taking/Common/useOngoingSection';
import useQuestionPagination from '@cont/Quiz/Taking/Common/useQuestionPagination';
import useQuestionStatus from '@cont/Quiz/RevampedTaking/Navigation/useQuestionStatus';
import useFailedAPIQueue from '@cont/Quiz/Taking/Common/useFailedAPIQueue';
import { useNtaExamSummary } from './NtaExamSummaryContext';
import { NTA_ALERTS, NTA_COLORS } from '../constants';
import { ntaButtonCss, ntaPageCss } from '../typography';

const ActionsWrap = styled.div`
  width: 100%;
  flex-shrink: 0;
  ${ntaPageCss}
`;

const PrimaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  padding: 16px 12px 12px;
  border-top: 1px solid ${NTA_COLORS.questionBorder};
`;

const SecondaryRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  background: ${NTA_COLORS.footerBg};
  border-top: 1px solid ${NTA_COLORS.footerBorder};
  border-bottom: 1px solid ${NTA_COLORS.footerBorder};
`;

const ActionButton = styled.button`
  height: 40px;
  padding: 0 12px;
  border-radius: 0;
  border: ${({ $bordered }) =>
    $bordered ? `1px solid ${NTA_COLORS.divider}` : 'none'};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color || NTA_COLORS.white};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  flex: ${({ $flex }) => $flex ?? '0 0 auto'};
  width: ${({ $width }) => $width ?? 'auto'};
  min-width: ${({ $minWidth }) => $minWidth ?? 'auto'};
  ${ntaButtonCss}
`;

const NavGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const NtaQuestionActions = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isPendingAction = useSelectorWithShallowEqual(getIsPendingAction);
  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const currentQuestion = useSelectorWithShallowEqual(getCurrentQuestion);
  const { sectionUID, questionUID } = useSelectorWithShallowEqual(
    getCurrentQuestionInfo
  );
  const answers = useSelectorWithShallowEqual(
    getQuizQuestionAnswers(sectionUID, questionUID)
  );
  const currentSelectedAnswer = useSelectorWithShallowEqual(
    getCurrentSelectedAnswer
  );

  const { addFailedSubmitAPI } = useFailedAPIQueue();
  const { isAttempted, isMarked } = useQuestionStatus(sectionUID, questionUID);
  const { isQuestionAttemptable } = useMaxAttemptQuestions(sectionUID);
  const { hasPrevious, hasNext, handleOnPrevious, handleOnNext } =
    useQuestionPagination();
  const { hasOngoingSectional, isLastOngoingSection } =
    useOngoingSection(sectionUID);
  const { open: showSnackbar } = useSnackbar();
  const { openSummary } = useNtaExamSummary() ?? {};

  const isSectionSubmit = hasOngoingSectional && !isLastOngoingSection;

  const isLastQuestion =
    currentQuestion.question_rank === quizDetails.num_questions;
  const isDisabled = isPendingAction;

  const persistAnswer = async (attemptList = currentSelectedAnswer) => {
    if (!arrayAreEqual(answers, attemptList)) {
      const data = {
        question_uid: questionUID,
        attempt_list: attemptList
      };

      const resp = await dispatch(
        submitAnswerResponse(quizUID, sectionUID, currentSessionUID, data)
      );

      if (resp.error) {
        addFailedSubmitAPI(data);
      }
    }
  };

  const toggleMark = async (nextMarked) => {
    const resp = await dispatch(
      toggleMarkQuestion(
        quizUID,
        currentSessionUID,
        sectionUID,
        questionUID,
        nextMarked
      )
    );

    if (resp.error) {
      showSnackbar({
        message: `Unable to update question ${currentQuestion.question_rank}. Please try again`,
        type: 'error'
      });
    }
  };

  const handleSaveAndNext = async () => {
    if (!isAttempted && !isQuestionAttemptable) return;

    if (currentSelectedAnswer.length === 0) {
      // eslint-disable-next-line no-alert
      window.alert(NTA_ALERTS.CHOOSE_OPTION);
      return;
    }

    await persistAnswer();
    if (isMarked) await toggleMark(false);
    if (!isLastQuestion) handleOnNext();
  };

  const handleSaveAndMarkForReview = async () => {
    if (!isAttempted && !isQuestionAttemptable) return;

    if (currentSelectedAnswer.length === 0) {
      // eslint-disable-next-line no-alert
      window.alert(NTA_ALERTS.CHOOSE_OPTION);
      return;
    }

    await persistAnswer();
    if (!isMarked) await toggleMark(true);
    if (!isLastQuestion) handleOnNext();
  };

  const handleMarkForReviewAndNext = async () => {
    if (!isMarked) await toggleMark(true);
    if (!isLastQuestion) handleOnNext();
  };

  const handleClearResponse = async () => {
    const data = {
      question_uid: questionUID,
      attempt_list: []
    };

    if (isAttempted) {
      const resp = await dispatch(
        submitAnswerResponse(quizUID, sectionUID, currentSessionUID, data)
      );
      if (resp.error) {
        addFailedSubmitAPI(data);
      } else {
        dispatch(setCurrentSelectedAnswer([]));
        if (isMarked) await toggleMark(false);
      }
    } else {
      dispatch(setCurrentSelectedAnswer([]));
      if (isMarked) await toggleMark(false);
    }
  };

  const handleSubmit = () => {
    openSummary?.(
      isSectionSubmit ? ATTEMPT_SUMMARY_TYPE.SECTION : ATTEMPT_SUMMARY_TYPE.TEST
    );
  };

  const canClear = currentSelectedAnswer.length > 0 || isAttempted;

  return (
    <ActionsWrap>
      <PrimaryRow>
        <ActionButton
          type="button"
          $bg={NTA_COLORS.actionSaveNext}
          $width="130px"
          $disabled={isDisabled}
          disabled={isDisabled}
          onClick={handleSaveAndNext}
        >
          {isLastQuestion ? 'SAVE' : 'SAVE & NEXT'}
        </ActionButton>
        <ActionButton
          type="button"
          $bg={NTA_COLORS.actionSaveMark}
          $flex="1 1 0"
          $minWidth="160px"
          $disabled={isDisabled}
          disabled={isDisabled}
          onClick={handleSaveAndMarkForReview}
        >
          SAVE & MARK FOR REVIEW
        </ActionButton>
        <ActionButton
          type="button"
          $bg={NTA_COLORS.white}
          $color={NTA_COLORS.textPrimary}
          $bordered
          $width="148px"
          $disabled={isDisabled || !canClear}
          disabled={isDisabled || !canClear}
          onClick={handleClearResponse}
        >
          CLEAR RESPONSE
        </ActionButton>
        <ActionButton
          type="button"
          $bg={NTA_COLORS.actionMarkNext}
          $flex="1 1 0"
          $minWidth="160px"
          $disabled={isDisabled}
          disabled={isDisabled}
          onClick={handleMarkForReviewAndNext}
        >
          {isLastQuestion ? 'MARK FOR REVIEW' : 'MARK FOR REVIEW & NEXT'}
        </ActionButton>
      </PrimaryRow>
      <SecondaryRow>
        <NavGroup>
          <ActionButton
            type="button"
            $bg={NTA_COLORS.white}
            $color={NTA_COLORS.actionNavText}
            $bordered
            $disabled={isDisabled || !hasPrevious}
            disabled={isDisabled || !hasPrevious}
            onClick={handleOnPrevious}
          >
            {'<< BACK'}
          </ActionButton>
          <ActionButton
            type="button"
            $bg={NTA_COLORS.white}
            $color={NTA_COLORS.textPrimary}
            $bordered
            $disabled={isDisabled || !hasNext}
            disabled={isDisabled || !hasNext}
            onClick={handleOnNext}
          >
            {'NEXT >>'}
          </ActionButton>
        </NavGroup>
        <ActionButton
          type="button"
          $bg={NTA_COLORS.actionSubmit}
          $width="135px"
          $disabled={isDisabled}
          disabled={isDisabled}
          onClick={handleSubmit}
        >
          {isSectionSubmit ? 'SUBMIT SECTION' : 'SUBMIT'}
        </ActionButton>
      </SecondaryRow>
    </ActionsWrap>
  );
};

export default NtaQuestionActions;
